import { isApiError } from '@/api/fetchWrapper';
import type {
  ApplyJobDetails,
  ApplyJobDuplicateBody,
  UnfinishedInterview,
} from '@/api/types';

type Position = ApplyJobDetails['positions'][number];

/** The status the apply POST answers with when the visitor already applied. */
export const DUPLICATE_APPLICATION_STATUS = 409;

export const isDuplicateApplicationError = (error: unknown): boolean =>
  isApiError(error) && error.status === DUPLICATE_APPLICATION_STATUS;

/**
 * Turns a 409 into an `UnfinishedInterview` the rest of the apply screen
 * already knows how to render — the same shape the details GET returns, so it
 * flows straight into `buildPositionOptions` and reaches `ContinueGate`
 * without a second code path.
 *
 * Returns null whenever the body carries no `invitation_key`: without a key
 * there is nothing to continue *to*, and a keyless entry would be dropped
 * downstream anyway (`buildPositionOptions` filters those out). A null result
 * is the caller's signal to fall back to refetching the details.
 *
 * The body shape is not a settled contract, hence the defensive read: a plain
 * text 409, an HTML error page, or a JSON object without the key all land on
 * null rather than throwing.
 */
export function parseDuplicateApplication(
  error: unknown,
  submittedPositionId: string,
  positions: Position[] | undefined
): UnfinishedInterview | null {
  if (!isDuplicateApplicationError(error)) return null;

  const body = isApiError(error) ? error.body : undefined;
  if (!body || typeof body !== 'object') return null;

  const { invitation_key, position_id, position_name } =
    body as ApplyJobDuplicateBody;
  if (!invitation_key) return null;

  // The submitted value is the more trustworthy id: it is the position the
  // applicant actually chose. The body's own `position_id` is a fallback for a
  // backend that echoes it back but ignores what was sent.
  //
  // The blank check is not redundant — `Number('')` is 0, a finite number that
  // would silently pass for a real position id and match nothing.
  const submitted =
    submittedPositionId.trim() === '' ? NaN : Number(submittedPositionId);
  // NaN rather than a cast when neither source has an id: it keeps the field a
  // `number` while staying detectably absent to `Number.isFinite` checks.
  const resolvedId = Number.isFinite(submitted) ? submitted : Number(position_id);

  return {
    invitation_key,
    position_id: resolvedId,
    // `buildPositionOptions` prefers the open position's own localized label
    // and only falls back to this, so it matters solely when the role has
    // closed. Keep the local lookup anyway so the name is right in that case.
    position_name:
      positions?.find((position) => position.value === resolvedId)?.label ??
      position_name ??
      '',
  };
}

/**
 * Adds a locally-derived unfinished interview to the list the server reported.
 *
 * Deduped on either identifier: once a refetch reports the same interview, the
 * server's copy wins and the local one is dropped, so the gate can't show the
 * same role twice.
 */
export function mergeUnfinished(
  list: UnfinishedInterview[] | undefined,
  extra: UnfinishedInterview | null
): UnfinishedInterview[] | undefined {
  if (!extra) return list;
  const existing = list ?? [];
  const alreadyThere = existing.some(
    (item) =>
      item.invitation_key === extra.invitation_key ||
      item.position_id === extra.position_id
  );
  return alreadyThere ? existing : [...existing, extra];
}
